<script>
import { mapGetters } from 'vuex';
import LabelDropdownItem from 'shared/components/ui/label/LabelDropdownItem.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { picoSearch } from '@scmmishra/pico-search';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LabelDropdownItem,
    AddLabelModal,
    NextButton,
  },
  props: {
    selectedLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  setup() {
    return {};
  },
  data() {
    return {
      search: '',
      createModalVisible: false,
      showDropdown: false,
      dropdownRef: null,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  computed: {
    ...mapGetters({
      accountLabels: 'labels/getLabels',
    }),
    createLabelPlaceholder() {
      const label = this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
      return this.search ? `${label}:` : label;
    },
    filteredActiveLabels() {
      if (!this.search) return this.accountLabels;
      return picoSearch(this.accountLabels, this.search, ['title'], {
        threshold: 0.9,
      });
    },
    noResult() {
      return this.filteredActiveLabels.length === 0;
    },
    hasExactMatchInResults() {
      return this.filteredActiveLabels.some(
        label => label.title === this.search
      );
    },
    shouldShowCreate() {
      return (
        this.filteredActiveLabels.length < 3 && this.search.trim().length > 0
      );
    },
    parsedSearch() {
      return sanitizeLabel(this.search);
    },
    selectedLabelObject() {
      if (!this.selectedLabel) return null;
      return this.accountLabels.find(l => l.title === this.selectedLabel);
    },
  },
  methods: {
    getTextColor(hex = '#000000') {
      if (!hex) return '#000';
      const c = hex.replace('#', '');
      const rgb = parseInt(c, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? '#111827' : '#ffffff';
    },
    onLabelSelect(label) {
      if (this.selectedLabel === label.title) {
        this.$emit('update', '');
      } else {
        this.$emit('update', label.title);
      }
      this.showDropdown = false;
      this.search = '';
    },
    showCreateModal() {
      this.createModalVisible = true;
    },
    hideCreateModal() {
      this.createModalVisible = false;
      this.showDropdown = false;
      this.search = '';
    },
    async onLabelCreated() {
      await this.$store.dispatch('labels/get');
      await this.$nextTick();
      const newLabel = this.accountLabels.find(
        l => l.title === this.parsedSearch
      );
      if (newLabel) {
        this.$emit('update', newLabel.title);
      }
      this.hideCreateModal();
    },
    removeLabel() {
      this.$emit('update', '');
    },
    handleClickOutside(event) {
      if (
        this.showDropdown &&
        this.$refs.dropdownContainer &&
        !this.$refs.dropdownContainer.contains(event.target)
      ) {
        this.showDropdown = false;
        this.search = '';
      }
    },
  },
};
</script>

<template>
  <div class="relative">
    <div v-if="selectedLabelObject" class="flex items-center gap-2 mb-2">
      <div
        class="px-3 py-1 h-7 flex items-center rounded-full border text-xs font-semibold capitalize transition border-solid"
        :style="{
          backgroundColor: selectedLabelObject.color || '#ddd',
          borderColor: selectedLabelObject.color || '#ddd',
          color: getTextColor(selectedLabelObject.color || '#000'),
        }"
      >
        {{ selectedLabelObject.title }}
      </div>
      <button
        type="button"
        class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        @click="removeLabel"
      >
        {{ $t('INBOX_MGMT.LABELS.REMOVE') || 'Remove' }}
      </button>
    </div>

    <div ref="dropdownContainer" class="relative">
      <input
        v-model="search"
        type="text"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-gray-400"
        :placeholder="
          $t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER') ||
          'Search or create label...'
        "
        @focus="showDropdown = true"
      />

      <div
        v-if="showDropdown"
        class="absolute z-50 w-full mt-1 bg-white dark:bg-n-solid-2 rounded-md shadow-lg max-h-64 overflow-auto"
      >
        <div class="p-2">
          <div v-if="filteredActiveLabels.length > 0">
            <LabelDropdownItem
              v-for="label in filteredActiveLabels"
              :key="label.id"
              :title="label.title"
              :color="label.color"
              :selected="selectedLabel === label.title"
              @select-label="onLabelSelect(label)"
            />
          </div>

          <div
            v-if="noResult"
            class="flex justify-center py-4 px-2.5 font-medium text-xs text-gray-500 dark:text-gray-400"
          >
            {{
              $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') ||
              'No labels found'
            }}
          </div>

          <div
            v-if="shouldShowCreate"
            class="flex pt-1 border-t border-solid border-gray-200 dark:border-gray-600"
          >
            <NextButton
              icon="i-lucide-plus"
              slate
              sm
              ghost
              :label="`${createLabelPlaceholder} ${parsedSearch}`"
              :disabled="hasExactMatchInResults"
              @click="showCreateModal"
            />
          </div>
        </div>
      </div>
    </div>

    <woot-modal v-model:show="createModalVisible" :on-close="hideCreateModal">
      <AddLabelModal
        :prefill-title="parsedSearch"
        @close="hideCreateModal"
        @created="onLabelCreated"
      />
    </woot-modal>
  </div>
</template>
